import React, { useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { MarktingContext } from "../../Context/MarktingContext";
import SkeletonPDF from "../LoadingPages/SkeletonPDF";
import css from "./style.module.css";
import { useNavigate } from "react-router-dom";

export default function ImageComponent({
    id,
    imgUrl,
    loginToken,
    comments,
    setActiveComment,
    archived,
    setDocumentStatus,
    activeCommentFromFeed // New prop to receive the active comment from DocumentComments
}) {
    const { setMarktingData, marktingData } = useContext(MarktingContext);

    const [imageUrl, setImageUrl] = useState("");
    const [showInput, setShowInput] = useState(false);
    const [inputPosition, setInputPosition] = useState({ x: 0, y: 0 });
    const [formPosition, setFormPosition] = useState({ top: 0, left: 0 });
    const [visibleCommentId, setVisibleCommentId] = useState(null);
    const [size, setSize] = useState({ width: 0, height: 0 });
    const [currentCommentText, setCurrentCommentText] = useState('');
    const [highlightedCommentId, setHighlightedCommentId] = useState(null); // New state for highlighting

    const containerRef = useRef(null);
    const imageRef = useRef(null);
    const commentRefs = useRef({}); // New ref to store comment element references

    const navigation = useNavigate();

    // New effect to handle activeCommentFromFeed changes
    useEffect(() => {
        if (activeCommentFromFeed && activeCommentFromFeed.id) {
            const commentElement = commentRefs.current[activeCommentFromFeed.id];
            if (commentElement) {
                // Scroll the comment into view
                commentElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
                
                // Highlight the comment
                setHighlightedCommentId(activeCommentFromFeed.id);
                setVisibleCommentId(activeCommentFromFeed.id);
                
                // Remove highlight after 3 seconds
                setTimeout(() => {
                    setHighlightedCommentId(null);
                }, 3000);
            }
        }
    }, [activeCommentFromFeed]);

    const calculateFormPosition = (clickEvent) => {
        const formWidth = 320;
        const formHeight = 200; // Approximate height of the form
        const padding = 10;

        // Get viewport dimensions
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Get the container element (overflow-auto div)
        const container = clickEvent.currentTarget.closest('.overflow-auto') ||
            clickEvent.currentTarget.closest('[style*="overflowY: auto"]') ||
            containerRef.current;
        const containerRect = container ? container.getBoundingClientRect() : {
            top: 0,
            left: 0,
            right: viewportWidth,
            bottom: viewportHeight
        };

        // Get click position relative to viewport
        let x = clickEvent.clientX;
        let y = clickEvent.clientY;

        // Calculate preferred position (to the right and below the click)
        let left = x + padding;
        let top = y + padding;

        // Adjust horizontal position if form would go outside viewport or container
        if (left + formWidth > Math.min(viewportWidth, containerRect.right) - padding) {
            left = x - formWidth - padding; // Position to the left of click
        }
        if (left < Math.max(0, containerRect.left) + padding) {
            left = Math.max(0, containerRect.left) + padding; // Align to left edge with padding
        }

        // Adjust vertical position if form would go outside viewport or container
        if (top + formHeight > Math.min(viewportHeight, containerRect.bottom) - padding) {
            top = Math.max(y - formHeight - padding, Math.max(0, containerRect.top) + padding); // Position above click or at top
        }
        if (top < Math.max(0, containerRect.top) + padding) {
            top = Math.max(0, containerRect.top) + padding; // Align to top edge with padding
        }

        // Ensure the form stays within the container bounds
        left = Math.max(containerRect.left + padding, Math.min(left, containerRect.right - formWidth - padding));
        top = Math.max(containerRect.top + padding, Math.min(top, containerRect.bottom - formHeight - padding));

        return { top, left };
    };

    const updateSize = () => {
        if (imageRef.current) {
            setSize({
                width: imageRef.current.offsetWidth,
                height: imageRef.current.offsetHeight,
            });
        }
    };

    useEffect(() => {
        if (imgUrl) {
            setImageUrl(imgUrl);
        }
    }, [imgUrl]);

    const handleContentClick = (e) => {
        if (!imageRef.current || archived) return;

        const rect = imageRef.current.getBoundingClientRect();
        const relativeX = e.clientX - rect.left;
        const relativeY = e.clientY - rect.top;

        // Calculate form position using the new function
        const calculatedPosition = calculateFormPosition(e, imageRef.current);

        setInputPosition({ x: relativeX, y: relativeY });
        setFormPosition(calculatedPosition);
        setShowInput(true);
    };

    const handleSaveComment = async (comment) => {
        try {
            if (!comment.trim()) return;

            const payload = {
                XLine: inputPosition.x,
                YLine: inputPosition.y,
                screenWidth: size.width,
                screenHeight: size.height,
                comment: comment.trim(),
            };

            const { data } = await axios.post(
                `${process.env.REACT_APP_API_URL}/comment`,
                payload,
                { headers: { Authorization: `Bearer ${loginToken}` }, params: { id } }
            );

            if (data?.data) {
                const deepClone = JSON.parse(JSON.stringify(marktingData));

                // Find the element with matching documentId or versionId
                const element = deepClone.find((item) =>
                    item.documentId === id ||
                    item.version?.some(version => version.versionId === id)
                );

                if (!element) {
                    return; // If no matching element, exit the function
                }

                if (element.documentId === id) {
                    if (!element.Comments) {
                        element.Comments = [data.data];
                    } else {
                        element.Comments.push(data.data);
                    }
                    element.status = data.status;
                } else {
                    const version = element.version.find(v => v.versionId === id);
                    version.Comments = version.Comments || [];
                    version.Comments.push(data.data);
                    version.status = data.status;
                }
                setDocumentStatus(data.status);

                setMarktingData(deepClone);
            }

            setShowInput(false);
            setCurrentCommentText('');
        } catch (error) {
            if (error?.response?.status === 401) {
                localStorage.removeItem("Firebase Auth");
                localStorage.removeItem("okta-token-storage");
                localStorage.removeItem("okta-cache-storage");
                navigation('/');
                return;
            }
        }
    };

    const handleCancelComment = () => {
        setShowInput(false);
        setCurrentCommentText('');
    };

    if (!imageUrl) {
        return <SkeletonPDF />;
    }

    return (
        <div
            ref={containerRef}
            style={{
                position: "relative",
                maxHeight: "100vh",
                overflowY: "auto",
                padding: "10px",
                borderRadius: "5px",
                cursor: !archived ? "crosshair" : "default",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            }}
            className="h-100"
        >
            <div
                style={{
                    position: "relative",
                    display: "inline-block",
                    width: "100%",
                    aspectRatio: "16/9",
                }}
                onClick={handleContentClick}
            >
                <img
                    ref={imageRef}
                    src={imageUrl}
                    alt="file"
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                    }}
                    className="img-fluid rounded-4"
                    onLoad={updateSize}
                />

                {comments?.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)).map((comment, index) => {
                    const scaleX = size.width / comment.screenWidth;
                    const scaleY = size.height / comment.screenHeight;
                    const adjustedX = comment.XLine * scaleX;
                    const adjustedY = comment.YLine * scaleY;
                    const isHighlighted = highlightedCommentId === comment.id;
                    
                    return (
                        <div
                            key={comment.id}
                            id={comment.id}
                            ref={(el) => {
                                if (el) {
                                    commentRefs.current[comment.id] = el;
                                }
                            }}
                            style={{
                                position: "absolute",
                                left: `${adjustedX}px`,
                                top: `${adjustedY}px`,
                                transform: "translate(-50%, -50%)",
                                cursor: "pointer",
                                zIndex: 999,
                                transition: "all 0.3s ease", // Smooth transition for highlight effect
                            }}
                        >
                            <span
                                style={{
                                    width: "30px",
                                    height: "30px",
                                    borderRadius: "50%",
                                    background: visibleCommentId === comment.id ? "var(--accent)" : "var(--ink-soft)",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    color: "var(--surface)",
                                    boxShadow: isHighlighted 
                                        ? "0 0 20px rgba(195, 29, 138, 0.8), 0 2px 4px rgba(0, 0, 0, 0.53)" 
                                        : "0 2px 4px rgba(0, 0, 0, 0.53)",
                                    transform: isHighlighted ? "scale(1.2)" : "scale(1)",
                                    transition: "all 0.3s ease",
                                    border: isHighlighted ? "3px solid var(--accent)" : "none",
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveComment(comment);
                                    setVisibleCommentId(comment.id);
                                }}
                            >
                                {index + 1}
                            </span>
                            
                            {/* Pulse animation for highlighted comment */}
                            {isHighlighted && (
                                <div
                                    style={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        width: "60px",
                                        height: "60px",
                                        borderRadius: "50%",
                                        border: "2px solid var(--accent)",
                                        animation: "pulse 2s infinite",
                                        pointerEvents: "none",
                                    }}
                                />
                            )}
                        </div>
                    );
                })}
            </div>

            {showInput && !archived && (
                <div
                    className="form-control"
                    style={{
                        position: 'fixed',
                        top: `${formPosition.top}px`,
                        left: `${formPosition.left}px`,
                        padding: '10px',
                        border: '1px solid #ccc',
                        zIndex: 1000,
                        width: '320px',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
                        backgroundColor: 'white',
                    }}
                >
                    <textarea
                        value={currentCommentText}
                        onChange={(e) => setCurrentCommentText(e.target.value)}
                        placeholder="Enter your comment here..."
                        style={{
                            width: '100%',
                            height: '130px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            padding: '8px',
                            resize: 'none'
                        }}
                        autoFocus
                    />
                    <div className="d-flex gap-2 mt-1">
                        <button
                            className="b-pink-color flex-grow-1"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSaveComment(currentCommentText);
                            }}
                        >
                            Save
                        </button>
                        <button
                            className="btn btn-outline-danger"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCancelComment();
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
            
            {/* CSS for pulse animation */}
            <style jsx>{`
                @keyframes pulse {
                    0% {
                        transform: translate(-50%, -50%) scale(1);
                        opacity: 1;
                    }
                    50% {
                        transform: translate(-50%, -50%) scale(1.5);
                        opacity: 0.5;
                    }
                    100% {
                        transform: translate(-50%, -50%) scale(2);
                        opacity: 0;
                    }
                }
            `}</style>
        </div>
    );
}